import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Play, Pause, CheckCircle2, Search, Database, 
  AlertTriangle, Filter, Zap, Clock, Server, FileText, ChevronRight
} from 'lucide-react';

const MOCK_STATS = [
  { label: 'Luật Kiểm tra Đang kích hoạt', value: '1,248', icon: FileText, color: 'text-indigo-600 dark:text-indigo-400', bg: 'bg-indigo-500/10' },
  { label: 'Hệ thống Đang Quét (Nodes)', value: '14', icon: Server, color: 'text-cyan-600 dark:text-cyan-400', bg: 'bg-cyan-500/10' },
  { label: 'Truy vấn mỗi giây (QPS)', value: '3,842', icon: Zap, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-500/10' },
  { label: 'Cảnh báo Phát hiện Hôm nay', value: '86', icon: AlertTriangle, color: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-500/10' },
];

const MOCK_RUNNING_SCANS = [
  { id: 'SCN-8823', org: 'Bộ Y Tế (BYT)', target: 'CSDL Quốc gia về Dân số', progress: 78, status: 'scanning', timeRem: '2m 15s', found: 12 },
  { id: 'SCN-8824', org: 'Bảo hiểm XH Việt Nam', target: 'Hệ thống Thanh toán BHYT', progress: 45, status: 'scanning', timeRem: '8m 40s', found: 4 },
  { id: 'SCN-8821', org: 'UBND TP. Hà Nội', target: 'Cổng Dịch vụ công HN', progress: 100, status: 'completed', timeRem: 'Hoàn thành', found: 23 },
];

const MOCK_HISTORY = [
  { date: '2026-03-20 08:30:00', scanId: 'SCN-8820', org: 'Bộ Công an', time: '14m 20s', rules: 450, passRate: 98, violations: 2 },
  { date: '2026-03-19 22:15:00', scanId: 'SCN-8819', org: 'Bộ Giáo dục', time: '08m 15s', rules: 210, passRate: 85, violations: 31 },
  { date: '2026-03-19 14:00:00', scanId: 'SCN-8818', org: 'UBND Tỉnh Lào Cai', time: '05m 40s', rules: 120, passRate: 92, violations: 9 },
  { date: '2026-03-18 09:45:00', scanId: 'SCN-8817', org: 'Bộ Tài chính', time: '22m 10s', rules: 580, passRate: 99, violations: 1 },
];

export default function AutoAuditPage() {
  const [isScanning, setIsScanning] = useState(true);

  useEffect(() => {
    console.log('AutoAuditPage component mounted');
  }, []);

  return (
    <div className="space-y-8 animate-in fade-in duration-500 relative">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
         <div>
            <h2 className="text-3xl font-heading font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-3 leading-tight">
              <Zap className="text-cyan-600 dark:text-cyan-400" size={32} />
              Quét Tự động Core Logic
            </h2>
            <p className="text-slate-500 dark:text-slate-400 mt-2 max-w-2xl font-black tracking-tight text-xs leading-relaxed">
              Hệ thống Data Quality & Legal Rule Engine cấp quốc gia. Sử dụng cơ chế phân tán để đánh giá sự tuân thủ trên hàng triệu bản ghi theo thời gian thực.
            </p>
         </div>
         <div className="flex items-center gap-3">
            <button 
              className={`relative overflow-hidden px-6 py-2.5 rounded-xl text-[10px] font-black tracking-[0.2em] transition-all duration-300 flex items-center gap-2 shadow-lg active:scale-95 ${
                isScanning 
                  ? 'bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-500/50 hover:bg-rose-100 dark:hover:bg-rose-500/20'
                  : 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/50 hover:bg-emerald-100 dark:hover:bg-emerald-500/20'
              }`}
              onClick={() => setIsScanning(!isScanning)}
            >
              {isScanning ? (
                <><Pause size={18} /> Tạm dừng Toàn cầu</>
              ) : (
                <><Play size={18} /> Kích hoạt Engine</>
              )}
            </button>
         </div>
      </div>

      {/* STATS ROW */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {MOCK_STATS.map((stat, idx) => (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            key={idx} 
            className="glass-panel p-6 flex items-center gap-5 border-slate-200 dark:border-white/5 bg-white dark:bg-white/[0.02] shadow-sm transform hover:-translate-y-1 transition-all group"
          >
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${stat.bg} ${stat.color} border border-slate-100 dark:border-white/5 shadow-inner group-hover:scale-110 transition-transform`}>
              <stat.icon size={22} />
            </div>
            <div>
              <p className="text-2xl font-heading font-black text-slate-900 dark:text-white tracking-tighter">{stat.value}</p>
              <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 tracking-[0.2em] mt-1">{stat.label}</p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* TWO COLUMNS */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* LEFT COL: RUNNING SCANS */}
        <div className="lg:col-span-4 space-y-6">
          <div className="flex items-center justify-between px-2">
            <h3 className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 tracking-[0.25em] flex items-center gap-2">
              <Clock size={16} /> Tiến trình Đang thực thi
            </h3>
            <span className="bg-indigo-600 dark:bg-indigo-500 text-white text-[9px] font-black px-2 py-0.5 rounded-lg border border-indigo-500 shadow-lg shadow-indigo-500/20">
              {MOCK_RUNNING_SCANS.filter(s => s.status === 'scanning').length} Active
            </span>
          </div>

          <div className="space-y-4">
            {MOCK_RUNNING_SCANS.map((scan, idx) => (
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.15 }}
                key={scan.id} 
                className="glass-panel p-6 relative overflow-hidden group border-slate-200 dark:border-white/5 bg-white dark:bg-white/[0.02] shadow-sm rounded-3xl"
              >
                {/* Background glow */}
                {scan.status === 'scanning' && isScanning && (
                  <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/5 dark:bg-cyan-500/10 rounded-full blur-3xl" />
                )}

                <div className="flex justify-between items-start mb-5 relative z-10">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <p className="text-sm font-black text-slate-900 dark:text-white tracking-tight truncate pr-2">{scan.org}</p>
                      {scan.status === 'scanning' && isScanning ? (
                        <div className="w-2.5 h-2.5 rounded-full bg-cyan-500 dark:bg-cyan-400 animate-pulse shadow-[0_0_10px_rgba(6,182,212,0.5)]" />
                      ) : scan.status === 'completed' ? (
                        <CheckCircle2 size={14} className="text-emerald-500 dark:text-emerald-400" />
                      ) : (
                        <div className="w-2 h-2 rounded-full bg-amber-500" />
                      )}
                    </div>
                    <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 flex items-center gap-1.5 tracking-tighter">
                      <Database size={12} className="opacity-50" /> {scan.target}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[9px] font-black text-slate-300 dark:text-slate-600 tracking-widest">{scan.id}</p>
                    <p className="text-[11px] font-black text-rose-600 dark:text-rose-400 mt-1 tracking-tighter">{scan.found} Vi phạm</p>
                  </div>
                </div>

                <div className="space-y-3 relative z-10">
                  <div className="flex justify-between text-[10px] font-black tracking-widest">
                    <span className="text-slate-400 dark:text-slate-500">Mức độ hoàn thành</span>
                    <span className="text-cyan-600 dark:text-cyan-400">{scan.progress}%</span>
                  </div>
                  <div className="h-2 w-full bg-slate-100 dark:bg-black/40 rounded-full overflow-hidden shadow-inner border border-slate-200 dark:border-transparent">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: `${scan.progress}%` }}
                      transition={{ duration: 1.5, ease: "easeOut" }}
                      className={`h-full rounded-full ${scan.status === 'completed' ? 'bg-emerald-500 shadow-emerald-500/20' : 'bg-cyan-500 relative shadow-cyan-500/20'}`}
                    >
                       {scan.status === 'scanning' && isScanning && (
                         <div className="absolute top-0 left-0 w-full h-full bg-white/30 animate-[scanning_1.5s_ease-in-out_infinite]" />
                       )}
                    </motion.div>
                  </div>
                  <div className="flex justify-between items-center pt-2">
                    <span className="text-[9px] font-black text-slate-400 dark:text-slate-500 tracking-widest flex items-center gap-1.5">
                      <Clock size={12} className="opacity-40" /> {scan.status === 'scanning' ? `ETA: ${scan.timeRem}` : `Dừng tại: ${scan.timeRem}`}
                    </span>
                    <button className="text-[9px] font-black text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-white tracking-widest transition-colors flex items-center gap-1">
                      Báo cáo <ChevronRight size={12} />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* RIGHT COL: HISTORY TABLE */}
        <div className="lg:col-span-8 space-y-6">
          <div className="glass-panel p-8 h-full flex flex-col border-slate-200 dark:border-white/5 bg-white dark:bg-white/[0.01] shadow-sm rounded-[2.5rem]">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8 relative z-10">
              <h3 className="text-[11px] font-black text-slate-900 dark:text-white flex items-center gap-3 tracking-[0.25em]">
                <div className="p-2 bg-emerald-500/10 rounded-xl">
                  <CheckCircle2 size={18} className="text-emerald-600 dark:text-emerald-400" />
                </div>
                Lịch sử Đánh giá Logic
              </h3>
              <div className="flex gap-3 w-full md:w-auto">
                <div className="flex items-center gap-2 bg-slate-50 dark:bg-black/20 border border-slate-200 dark:border-white/5 rounded-xl px-4 py-2 flex-1 md:w-64 focus-within:border-indigo-500/50 transition-all shadow-inner">
                  <Search size={14} className="text-slate-400" />
                  <input type="text" placeholder="Tra cứu mã lệnh/đơn vị..." className="bg-transparent text-xs outline-none w-full text-slate-900 dark:text-white font-medium" />
                </div>
                <button className="p-2.5 rounded-xl border border-slate-200 dark:border-white/10 text-slate-400 dark:text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-white/5 transition-all shadow-sm">
                  <Filter size={18} />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 dark:border-white/5 bg-slate-50/50 dark:bg-white/[0.01] text-[9px] font-black text-slate-400 dark:text-slate-500 tracking-[0.2em]">
                    <th className="px-6 py-4 text-left">Thời điểm Quét</th>
                    <th className="px-6 py-4 text-left">Mã lệnh</th>
                    <th className="px-6 py-4 text-left">Cơ quan thụ hưởng</th>
                    <th className="px-6 py-4 text-center">Engine Rules</th>
                    <th className="px-6 py-4 text-center">Tỷ lệ Đạt</th>
                    <th className="px-6 py-4 text-right">Thao tác</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                  {MOCK_HISTORY.map((row) => (
                    <motion.tr 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      key={row.scanId} 
                      className="group hover:bg-slate-50 dark:hover:bg-white/[0.03] transition-all cursor-pointer"
                    >
                      <td className="px-6 py-5 whitespace-nowrap">
                        <p className="text-slate-900 dark:text-white font-black text-[11px] tracking-tight">{row.date.split(' ')[0]}</p>
                        <p className="text-[10px] text-slate-400 dark:text-slate-600 font-black tracking-widest mt-0.5">{row.date.split(' ')[1]}</p>
                      </td>
                      <td className="px-6 py-5 whitespace-nowrap">
                        <span className="bg-white dark:bg-white/5 border border-slate-100 dark:border-white/10 text-indigo-600 dark:text-indigo-400 font-mono font-black text-[10px] px-3 py-1.5 rounded-xl shadow-sm tracking-tighter">
                          {row.scanId}
                        </span>
                      </td>
                      <td className="px-6 py-5">
                        <p className="text-[13px] font-black text-slate-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors tracking-tight">{row.org}</p>
                        <p className="text-[10px] font-bold text-slate-400 mt-0.5 tracking-tighter opacity-70">Duration: {row.time}</p>
                      </td>
                      <td className="px-6 py-5 text-center">
                        <span className="text-[13px] font-black text-slate-700 dark:text-slate-300">{row.rules}</span>
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex items-center justify-center gap-2">
                           {row.passRate >= 90 ? (
                             <CheckCircle2 size={14} className="text-emerald-500 dark:text-emerald-400" />
                           ) : (
                             <AlertTriangle size={14} className="text-amber-500 dark:text-amber-400" />
                           )}
                           <span className={`font-black text-xs tracking-tighter ${row.passRate >= 90 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
                             {row.passRate}%
                           </span>
                        </div>
                      </td>
                      <td className="px-6 py-5 text-right">
                         <div className="flex items-center justify-end gap-3">
                           {row.violations > 0 && (
                              <span className="px-2.5 py-1 rounded-lg text-[9px] font-black bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/20 tracking-tighter shadow-sm">
                                {row.violations} Vi phạm
                              </span>
                           )}
                           <button className="px-4 py-2 bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-400 hover:bg-indigo-500 hover:text-white text-[9px] font-black tracking-[0.2em] rounded-xl transition-all shadow-sm active:scale-95">
                             Trích xuất
                           </button>
                         </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            <div className="mt-8 pt-6 border-t border-slate-100 dark:border-white/5 flex items-center justify-between text-[9px] font-black text-slate-400 dark:text-slate-500 tracking-widest">
               <span>Kết quả quét <span className="text-slate-900 dark:text-white">4 / 1,204</span> đã tải</span>
               <div className="flex gap-2">
                 <button className="px-3 py-1.5 hover:text-indigo-600 dark:hover:text-white transition-colors">Trước</button>
                 <button className="w-8 h-8 flex items-center justify-center bg-indigo-600 text-white rounded-xl shadow-lg shadow-indigo-500/20">1</button>
                 <button className="w-8 h-8 flex items-center justify-center bg-slate-50 dark:bg-white/5 rounded-xl border border-slate-100 dark:border-white/10 hover:bg-slate-100 dark:hover:bg-white/10 transition-all">2</button>
                 <button className="w-8 h-8 flex items-center justify-center bg-slate-50 dark:bg-white/5 rounded-xl border border-slate-100 dark:border-white/10 hover:bg-slate-100 dark:hover:bg-white/10 transition-all">3</button>
                 <button className="px-3 py-1.5 hover:text-indigo-600 dark:hover:text-white transition-colors">Sau</button>
               </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
